import {
  faAngleDown,
  faCircleEllipsis,
  faEllipsis,
} from '@fortawesome/pro-regular-svg-icons';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import Grow from '@mui/material/Grow';
import Skeleton from '@mui/material/Skeleton';
import MuiTableCell from '@mui/material/TableCell';
import React, { useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Accordion } from 'src/components/Accordion';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { Divider } from 'src/components/Divider';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { NoResults } from 'src/shared/NoResults';

import {
  TableCell,
  TableColLabel,
  TableColLabels,
  TableContent,
  TableRow,
  TableRowSelectionCell,
  TableRows,
  TableValueDisplay,
} from './Table';

type Row = {
  id: string | number;
  [key: string]: any;
};

export interface ColumnConfig {
  id: string;
  label: string;
  format?: (value: any, row: any) => React.ReactNode;
  sortable?: boolean;
  sortKey?: string;
  maxWidth?: string;
  minWidth?: string;
}

interface ActionConfig {
  icon: any;
  label: string;
  onClick?: (row: any) => void;
  href?: string | ((row: any) => string);
  showAction?: (row: any) => boolean;
  target?: string;
}

interface BatchActionConfig extends ActionConfig {
  onClick: () => void;
}

export interface DataTableProps {
  columns: ColumnConfig[];
  rows?: Row[];
  allSelected?: boolean;
  setAllSelected?: (allSelected: boolean) => void;
  selectableRows?: boolean;
  selectedRows?: Row[];
  sortBy?: ColumnConfig['id'];
  sortDir?: 'asc' | 'desc';
  setSortDir?: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>;
  setSortBy?: React.Dispatch<React.SetStateAction<ColumnConfig['id']>>;
  setSelectedRows?: React.Dispatch<React.SetStateAction<any[]>>;
  batchActions?: BatchActionConfig[];
  rowActions?:
    | ActionConfig[]
    | ((row: Row, rowIndex: number) => React.ReactNode);
  rowLinkTo?: (row: Row, rowIndex: number) => string;
  onClickRow?: (row: Row, rowIndex: number) => void;
  noResultsText?: React.ReactNode;
  noPermissionText?: React.ReactNode;
  itemsPerPage?: number;
  totalItems?: number;
  fixedActions?: boolean;
  loading: boolean;
  actionTitle?: string;
}

const hiddenOnSmall = {
  '@container (max-width: 500px)': {
    display: 'none',
  },
};
const hiddenOnLarge = {
  '@container (min-width: 501px)': {
    display: 'none',
  },
};

/**
 * The main DataTable. Use this over Table for most app purposes.
 */
export const DataTable: React.FC<DataTableProps> = ({
  rows,
  columns,
  rowActions,
  batchActions,
  selectableRows,
  noResultsText,
  noPermissionText,
  rowLinkTo,
  onClickRow,
  loading,
  selectedRows,
  setSelectedRows,
  sortBy,
  setSortBy,
  sortDir,
  setSortDir,
  allSelected,
  setAllSelected,
  totalItems,
  itemsPerPage,
  fixedActions,
  actionTitle,
}) => {
  const selectedIds = selectedRows?.map((row) => row.id) || [];
  const rowsIds = rows?.map((row) => row.id) || [];
  const allInPageSelected =
    !!rows?.length && rows.every((row) => selectedIds.includes(row.id));

  const actionsIsFunc = typeof rowActions === 'function';
  const bulkActionsPillRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const selectionLabel = allSelected
    ? `All Selected`
    : `${selectedRows?.length} Selected`;

  const toggleSelectedRow = (row) => {
    if (selectedRows?.includes(row)) {
      setSelectedRows &&
        setSelectedRows((prev) =>
          prev.filter((selectedRow) => row !== selectedRow)
        );
      return;
    }
    setSelectedRows && setSelectedRows((prev) => [...prev, row]);
  };

  const enabledRowActions = useCallback(
    (row: Row): ActionConfig[] => {
      if (Array.isArray(rowActions)) {
        return rowActions.filter(
          (action) => !action.showAction || action.showAction?.(row)
        );
      }
      return [] as ActionConfig[];
    },
    [rowActions]
  );

  const toggleSelectAllInPage = () => {
    if (!setSelectedRows || !rows?.length) {
      return;
    }
    if (allInPageSelected) {
      setSelectedRows((prev) =>
        prev.filter((row) => !rowsIds.includes(row.id))
      );
      setAllSelected && setAllSelected(false);
      return;
    }
    setSelectedRows((prev) => [
      ...prev,
      ...rows.filter((row) => !selectedIds.includes(row.id)),
    ]);
    setAllSelected && setAllSelected(false);
  };

  const toggleColSort = (col: ColumnConfig) => {
    if (!setSortDir || !setSortBy) {
      return;
    }
    const sortKeyToUse = col.sortKey || col.id;
    // Already sorting by this prop so toggle dir
    if (sortKeyToUse === sortBy) {
      if (sortDir === 'asc' || '') {
        return setSortDir('desc');
      }
      return setSortDir('asc');
    }
    setSortDir('desc');
    setSortBy(sortKeyToUse);
  };

  const triggerBounce = () => {
    bulkActionsPillRef.current?.classList.remove('bounce');
    bulkActionsPillRef.current?.classList.add('bounce');
    window.setTimeout(() => {
      bulkActionsPillRef.current?.classList.remove('bounce');
    }, 400);
  };

  if (!loading && (!rows || !rows.length)) {
    return (
      <Box sx={{ width: '100%' }}>
        <NoResults title={noResultsText} sx={{ width: '100%', mt: 3 }} />
        <Divider />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: '20px',
          left: '0px',
          width: '100vw',
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Grow in={!!selectedRows?.length}>
          <Stack
            spacing={0.5}
            direction="row"
            ref={bulkActionsPillRef}
            sx={{
              minWidth: '100px',
              bgcolor: 'neutral.800',
              color: 'neutral.50',
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto',
              '&.bounce': {
                animation: 'bounceToNotify 0.3s 1',
                '@keyframes bounceToNotify': {
                  '0%,': {
                    transform: 'scale(1)',
                  },
                  '40%': {
                    transform: 'scale(1.05) translateY(-8px)',
                  },
                  '100%': {
                    transform: 'scale(1)',
                  },
                },
              },
            }}
          >
            <IconButton
              onClick={() => {
                if (setSelectedRows) setSelectedRows([]);
                if (setAllSelected) setAllSelected(false);
              }}
            >
              <Icon sx={{ color: 'white' }} icon={faTimesCircle} />
            </IconButton>
            <DropdownMenu
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              trigger={
                <Button color="inherit">
                  {selectionLabel}
                  {noPermissionText ? <>.&nbsp;{noPermissionText}</> : null}
                  <Icon
                    size="xsmall"
                    sx={{ color: 'white', ml: 0.5 }}
                    icon={faAngleDown}
                  />
                </Button>
              }
            >
              <Stack spacing={1} sx={{ p: 1 }}>
                <ListItem
                  clickable
                  rounded
                  onClick={() => {
                    if (setSelectedRows) setSelectedRows(rows || []);
                    if (setAllSelected) setAllSelected(false);
                  }}
                  primary={`Select all on this page ${
                    itemsPerPage ? `(${itemsPerPage})` : ''
                  }`}
                ></ListItem>
                {setAllSelected && (
                  <ListItem
                    clickable
                    rounded
                    onClick={() => setAllSelected(true)}
                    primary={`Select all on all pages ${
                      totalItems ? `(${totalItems})` : ''
                    }`}
                  ></ListItem>
                )}
                <ListItem
                  clickable
                  rounded
                  primary="Deselect all"
                  onClick={() => {
                    if (setSelectedRows) setSelectedRows([]);
                    if (setAllSelected) setAllSelected(false);
                  }}
                ></ListItem>
              </Stack>
            </DropdownMenu>
            {!!batchActions?.length && (
              <DropdownMenu
                drawerOnMobile
                trigger={
                  <IconButton>
                    <Icon sx={{ color: 'white' }} icon={faCircleEllipsis} />
                  </IconButton>
                }
              >
                <Stack spacing={1} sx={{ p: 1 }}>
                  {batchActions.map((action, i) => (
                    <ListItem
                      key={action.label + i}
                      rounded
                      clickable
                      primary={action.label}
                      icon={action.icon}
                      onClick={action.onClick}
                    ></ListItem>
                  ))}
                </Stack>
              </DropdownMenu>
            )}
          </Stack>
        </Grow>
      </Box>
      <TableContent>
        <TableColLabels sx={hiddenOnSmall}>
          {selectableRows && (
            <TableRowSelectionCell
              disabled={allSelected}
              onChange={toggleSelectAllInPage}
              selected={allSelected || allInPageSelected}
              indeterminate={!!selectedRows?.length && !allSelected}
            />
          )}
          {columns.map((col) => (
            <TableColLabel
              key={col.id}
              sortable={!!col.sortable}
              sorting={sortBy === (col.sortKey || col.id) ? sortDir : undefined}
              onClick={() => toggleColSort(col)}
            >
              {col.label}
            </TableColLabel>
          ))}
          {!!rowActions && (
            <TableColLabel fixed={fixedActions} actions>
              {actionTitle || ' '}
            </TableColLabel>
          )}
        </TableColLabels>

        <TableRows sx={{ bgcolor: 'background.level1' }}>
          {loading && (
            <>
              <TableRow sx={hiddenOnSmall}>
                {selectableRows && (
                  <MuiTableCell component="div">
                    <Skeleton
                      width="24px"
                      height="24px"
                      variant="rectangular"
                    />
                  </MuiTableCell>
                )}
                {columns.map((column) => {
                  return (
                    <MuiTableCell component="div" key={column.id}>
                      <Skeleton width="100%" />
                    </MuiTableCell>
                  );
                })}
                {!!rowActions && (
                  <MuiTableCell component="div">
                    <Skeleton
                      width="40px"
                      height="24px"
                      variant="rectangular"
                    />
                  </MuiTableCell>
                )}
              </TableRow>
              <TableRow sx={hiddenOnSmall}>
                {selectableRows && (
                  <MuiTableCell component="div">
                    <Skeleton
                      width="24px"
                      height="24px"
                      variant="rectangular"
                    />
                  </MuiTableCell>
                )}
                {columns.map((column) => {
                  return (
                    <MuiTableCell component="div" key={column.id}>
                      <Skeleton width="100%" />
                    </MuiTableCell>
                  );
                })}
                {!!rowActions && (
                  <MuiTableCell component="div">
                    <Skeleton
                      width="40px"
                      height="24px"
                      variant="rectangular"
                    />
                  </MuiTableCell>
                )}
              </TableRow>
              <TableRow sx={hiddenOnSmall}>
                {selectableRows && (
                  <MuiTableCell component="div">
                    <Skeleton
                      width="24px"
                      height="24px"
                      variant="rectangular"
                    />
                  </MuiTableCell>
                )}
                {columns.map((column) => {
                  return (
                    <MuiTableCell component="div" key={column.id}>
                      <Skeleton width="100%" />
                    </MuiTableCell>
                  );
                })}
                {!!rowActions && (
                  <MuiTableCell component="div">
                    <Skeleton
                      width="40px"
                      height="24px"
                      variant="rectangular"
                    />
                  </MuiTableCell>
                )}
              </TableRow>
            </>
          )}

          {!loading &&
            rows?.map((row, rowIndex) => {
              const rowSelected = selectedRows?.includes(row);
              const onSelectRow = () => toggleSelectedRow(row);
              const rowLink = rowLinkTo && rowLinkTo(row, rowIndex);
              const disableRowClick = (selectedRows?.length || 0) > 0;

              const rowOnClick = () => {
                if (disableRowClick) {
                  triggerBounce();
                  return;
                }
                if (onClickRow) {
                  onClickRow(row, rowIndex);
                  return;
                }
                if (rowLink) {
                  navigate(rowLink);
                  return;
                }
              };

              return (
                <React.Fragment key={row.id}>
                  {/* Table at larger sizes */}
                  <TableRow
                    selected={allSelected || rowSelected}
                    onClick={rowOnClick}
                    key={row.id}
                    sx={[
                      hiddenOnSmall,
                      { textDecoration: 'none', width: '100%' },
                    ]}
                    hover={!!onClickRow || !!rowLinkTo}
                  >
                    {selectableRows && (
                      <TableRowSelectionCell
                        disabled={allSelected}
                        selected={allSelected || !!rowSelected}
                        onChange={onSelectRow}
                      />
                    )}
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell
                          key={column.id}
                          value={value}
                          formatValue={column.format}
                          maxWidth={column.maxWidth}
                          minWidth={column.minWidth}
                          row={row}
                        ></TableCell>
                      );
                    })}
                    {!!rowActions && (
                      <TableCell row={row} fixed={fixedActions} value actions>
                        {actionsIsFunc && rowActions(row, rowIndex)}
                        {!actionsIsFunc && !!enabledRowActions(row)?.length && (
                          <DropdownMenu
                            drawerOnMobile
                            trigger={
                              <IconButton>
                                <Icon icon={faEllipsis} />
                              </IconButton>
                            }
                          >
                            <Stack spacing={1} sx={{ p: 1 }}>
                              {enabledRowActions(row).map((action, i) => {
                                const href =
                                  typeof action.href === 'function'
                                    ? action.href(row)
                                    : action.href;
                                return (
                                  <ListItem
                                    key={action.label + i}
                                    rounded
                                    clickable
                                    component={href ? 'a' : undefined}
                                    href={href}
                                    target={
                                      href && action.target
                                        ? action.target
                                        : undefined
                                    }
                                    rel={
                                      href && action.target === '_blank'
                                        ? 'noopener noreferrer'
                                        : undefined
                                    }
                                    primary={action.label}
                                    icon={action.icon}
                                    onClick={
                                      href
                                        ? (e) => e?.stopPropagation()
                                        : () => action.onClick?.(row)
                                    }
                                  />
                                );
                              })}
                            </Stack>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                  {/* Accordions at smaller sizes */}
                  <Accordion
                    key={row.id + ' 1'}
                    label={
                      <Stack
                        direction="row"
                        spacing={2}
                        component={rowLinkTo ? Link : 'div'}
                        to={rowLinkTo && rowLinkTo(row, rowIndex)}
                        onClick={() => onClickRow && onClickRow(row, rowIndex)}
                        sx={{ textDecoration: 'none', color: 'unset' }}
                      >
                        {selectableRows && (
                          <Checkbox
                            disabled={allSelected}
                            checked={allSelected || rowSelected}
                            onChange={onSelectRow}
                            onClick={(e) => e.stopPropagation()}
                          ></Checkbox>
                        )}
                        <TableValueDisplay
                          row={row}
                          value={row[columns[0].id]}
                          formatValue={columns[0].format}
                        />
                      </Stack>
                    }
                    sx={[hiddenOnLarge, { width: '100%' }]}
                    expandedMargin
                  >
                    {columns.map((column, index) => {
                      const value = row[column.id];
                      if (index === 0) {
                        return null;
                      }
                      return (
                        <Typography sx={{ mb: 2 }} key={column.id}>
                          <Typography
                            color="text.secondary"
                            variant="body2"
                            gutterBottom
                          >
                            {column.label}
                          </Typography>
                          <TableValueDisplay
                            row={row}
                            value={value}
                            formatValue={column.format}
                          />
                        </Typography>
                      );
                    })}
                    {!!rowActions && (
                      <Stack direction="row-reverse" spacing={1}>
                        {actionsIsFunc && rowActions(row, rowIndex)}
                        {!actionsIsFunc && !!rowActions?.length && (
                          <DropdownMenu
                            drawerOnMobile
                            trigger={
                              <IconButton>
                                <Icon icon={faEllipsis} />
                              </IconButton>
                            }
                          >
                            <Stack spacing={1} sx={{ p: 1 }}>
                              {rowActions
                                .filter(
                                  (action) =>
                                    !action.showAction ||
                                    action.showAction?.(row)
                                )
                                .map((action, i) => {
                                  const href =
                                    typeof action.href === 'function'
                                      ? action.href(row)
                                      : action.href;
                                  return (
                                    <ListItem
                                      key={action.label + i}
                                      rounded
                                      clickable
                                      component={href ? 'a' : undefined}
                                      href={href}
                                      target={
                                        href && action.target
                                          ? action.target
                                          : undefined
                                      }
                                      rel={
                                        href && action.target === '_blank'
                                          ? 'noopener noreferrer'
                                          : undefined
                                      }
                                      primary={action.label}
                                      icon={action.icon}
                                      onClick={
                                        href
                                          ? (e) => e?.stopPropagation()
                                          : () => action.onClick?.(row)
                                      }
                                    />
                                  );
                                })}
                            </Stack>
                          </DropdownMenu>
                        )}
                      </Stack>
                    )}
                  </Accordion>
                </React.Fragment>
              );
            })}

          <Divider sx={hiddenOnLarge} />
        </TableRows>
      </TableContent>
    </>
  );
};
